import {
  LEAD_FIELDS,
  LEAD_REQUIREMENTS_MAX,
  NOTES_MIN_CHARS_MAX,
  type LeadEnforcement,
  type LeadField,
  type LeadRequirement,
  type LeadRulesResponse,
  type SaveLeadRulesInput,
} from '@viq-admin/types';
import { LEAD_RULES_COPY } from './copy';
import type { LeadRulesInventoryQuestion } from './types';

export interface LeadRulesDraft {
  enforcement: LeadEnforcement;
  requirements: LeadRequirement[];
}

export interface DraftIssue {
  requirementId?: string;
  message: string;
}

export type RequirementType = LeadRequirement['type'];

/** The notes minimum a new requirement starts at; the admin edits it. */
export const DEFAULT_NOTES_MIN = 500;

export function draftFromResponse(saved: LeadRulesResponse): LeadRulesDraft {
  return { enforcement: saved.enforcement, requirements: saved.requirements };
}

export function isDraftDirty(draft: LeadRulesDraft, saved: LeadRulesResponse): boolean {
  return JSON.stringify(draft) !== JSON.stringify(draftFromResponse(saved));
}

export function toSaveInput(draft: LeadRulesDraft, version: number): SaveLeadRulesInput {
  return { enforcement: draft.enforcement, requirements: draft.requirements, expectedVersion: version };
}

function usedQuestionIds(draft: LeadRulesDraft): Set<string> {
  return new Set(draft.requirements.flatMap((r) => (r.type === 'question_answered' ? [r.questionId] : [])));
}

function usedFields(draft: LeadRulesDraft): Set<string> {
  return new Set(draft.requirements.flatMap((r) => (r.type === 'field_present' ? [r.field] : [])));
}

export function addableTypes(draft: LeadRulesDraft, inventory: LeadRulesInventoryQuestion[]): RequirementType[] {
  if (draft.requirements.length >= LEAD_REQUIREMENTS_MAX) return [];
  const out: RequirementType[] = [];
  if (!draft.requirements.some((r) => r.type === 'notes_min_chars')) out.push('notes_min_chars');
  const questions = usedQuestionIds(draft);
  if (inventory.some((q) => !questions.has(q.id))) out.push('question_answered');
  const fields = usedFields(draft);
  if (LEAD_FIELDS.some((f) => !fields.has(f))) out.push('field_present');
  return out;
}

/** Only call with a type from addableTypes(); it picks the first unused question/field. */
export function newRequirement(
  type: RequirementType,
  draft: LeadRulesDraft,
  inventory: LeadRulesInventoryQuestion[],
  newId: () => string,
): LeadRequirement {
  switch (type) {
    case 'notes_min_chars':
      return { id: newId(), type, min: DEFAULT_NOTES_MIN };
    case 'question_answered': {
      const used = usedQuestionIds(draft);
      const question = inventory.find((q) => !used.has(q.id));
      if (!question) throw new Error('No unused inventory question to require');
      return { id: newId(), type, questionId: question.id };
    }
    case 'field_present': {
      const used = usedFields(draft);
      const field = LEAD_FIELDS.find((f) => !used.has(f));
      if (!field) throw new Error('No unused lead field to require');
      return { id: newId(), type, field };
    }
  }
}

export function updateRequirement(
  draft: LeadRulesDraft,
  id: string,
  patch: Partial<{ min: number; questionId: string; field: LeadField }>,
): LeadRulesDraft {
  return {
    ...draft,
    requirements: draft.requirements.map((r) => (r.id === id ? ({ ...r, ...patch } as LeadRequirement) : r)),
  };
}

export function removeRequirement(draft: LeadRulesDraft, id: string): LeadRulesDraft {
  return { ...draft, requirements: draft.requirements.filter((r) => r.id !== id) };
}

/** The checks the backend would refuse with 422, surfaced before Save. */
export function validateDraft(draft: LeadRulesDraft): DraftIssue[] {
  const issues: DraftIssue[] = [];
  if (draft.requirements.length > LEAD_REQUIREMENTS_MAX) issues.push({ message: LEAD_RULES_COPY.limitReached });
  const questions = new Set<string>();
  const fields = new Set<string>();
  let notesSeen = false;
  for (const r of draft.requirements) {
    if (r.type === 'notes_min_chars') {
      if (!Number.isInteger(r.min) || r.min < 1 || r.min > NOTES_MIN_CHARS_MAX) {
        issues.push({ requirementId: r.id, message: LEAD_RULES_COPY.notesRange });
      }
      // The backend refuses a second notes minimum. `addableTypes` stops the UI
      // offering one, but a draft loaded from stored rules can still carry two.
      if (notesSeen) issues.push({ requirementId: r.id, message: LEAD_RULES_COPY.duplicateNotes });
      notesSeen = true;
    } else if (r.type === 'question_answered') {
      if (questions.has(r.questionId)) issues.push({ requirementId: r.id, message: LEAD_RULES_COPY.duplicateQuestion });
      questions.add(r.questionId);
    } else {
      if (fields.has(r.field)) issues.push({ requirementId: r.id, message: LEAD_RULES_COPY.duplicateField });
      fields.add(r.field);
    }
  }
  return issues;
}

/** 'block' with nothing that can block: no notes minimum and no held field (spec 2026-09-28 §6). */
export function blockWithoutNotes(draft: LeadRulesDraft): boolean {
  return (
    draft.enforcement === 'block' &&
    !draft.requirements.some((r) => r.type === 'notes_min_chars' || (r.type === 'field_present' && r.hold === true))
  );
}

/** Spec 2026-09-28 §6: set or clear one field requirement's hold (the Console only). */
export function setRequirementHold(draft: LeadRulesDraft, id: string, hold: boolean): LeadRulesDraft {
  return {
    ...draft,
    requirements: draft.requirements.map((r) => {
      if (r.id !== id || r.type !== 'field_present') return r;
      return hold ? { ...r, hold: true } : { id: r.id, type: r.type, field: r.field };
    }),
  };
}
